import {TagFilter, PythonDocumentFilter, DocumentFilterSearchType} from "./boolean_filtering";
import {ColumnFiltersState} from "@tanstack/react-table";

export const CURRENT_FILTER_CONFIG_VERSION = 1;

export interface SearchFilterConfig {
    version: number;
    document_filters: PythonDocumentFilter[];
    tag_filters: TagFilter[];
}

export interface SavedSearchCreate {
    name: string;
    description?: string;
    group_id: string;
    filter_config: SearchFilterConfig;
    natural_language_query?: string;
}

export interface SavedSearchUpdate {
    id: string;
    name?: string;
    description?: string;
    filter_config?: SearchFilterConfig;
    natural_language_query?: string;
}

export interface SavedSearch {
    id: string;
    group_id: string;
    submitter_id: string;
    name: string;
    description?: string;
    filter_config: SearchFilterConfig;
    natural_language_query?: string;
}

export interface SavedSearchResults {
    saved_searches: SavedSearch[];
}

/**
 * Build a SearchFilterConfig from the current grid column filters and tag filters.
 * This is used when saving the current filter state as a saved search.
 */
export function buildFilterConfigFromGridState(
    columnFilters: ColumnFiltersState,
    tagFilters: TagFilter[]
): SearchFilterConfig {
    const documentFilters: PythonDocumentFilter[] = columnFilters
        .filter(filter => filter.value != null && filter.value !== "")
        .map(filter => ({
            field_name: filter.id,
            field_value: String(filter.value),
            search_type: "CONTAINS" as DocumentFilterSearchType,
        }));

    return {
        version: CURRENT_FILTER_CONFIG_VERSION,
        document_filters: documentFilters,
        tag_filters: tagFilters,
    };
}

/**
 * Convert a SearchFilterConfig to column filters state for the grid.
 * Note: This loses search_type information for legacy compatibility.
 */
export function filterConfigToColumnFilters(config: SearchFilterConfig): ColumnFiltersState {
    return config.document_filters.map(filter => ({
        id: filter.field_name,
        value: filter.field_value,
    }));
}

/**
 * Multi-filter support: ColumnFilter type for the advanced filter UI.
 * This interface is re-exported here for convenience but is defined in filter-types.ts.
 */
export interface MultiColumnFilter {
    id: string;
    columnId: string;
    value: string;
    searchType: DocumentFilterSearchType;
}

/**
 * Build a SearchFilterConfig from multi-filter state.
 * Preserves search_type for each filter.
 */
export function buildFilterConfigFromMultiFilters(
    filters: MultiColumnFilter[],
    tagFilters: TagFilter[]
): SearchFilterConfig {
    const documentFilters: PythonDocumentFilter[] = filters
        .filter(f => f.value != null && f.value.trim() !== "")
        .map(f => ({
            field_name: f.columnId,
            field_value: f.value.trim(),
            search_type: f.searchType,
        }));

    return {
        version: CURRENT_FILTER_CONFIG_VERSION,
        document_filters: documentFilters,
        tag_filters: tagFilters,
    };
}

/**
 * Convert a SearchFilterConfig to multi-filter state.
 * Preserves search_type for each filter.
 */
export function filterConfigToMultiFilters(config: SearchFilterConfig): MultiColumnFilter[] {
    return config.document_filters.map((filter, index) => ({
        id: `restored-${index}-${Date.now()}`,
        columnId: filter.field_name,
        value: filter.field_value,
        searchType: filter.search_type ?? "CONTAINS",
    }));
}